import React from 'react'
import { X, Download, Trash2, Clock, CheckCircle, XCircle } from 'lucide-react'

export interface ApiCall {
  id: string
  timestamp: Date
  request: {
    type: string
    sourceLanguage: string
    targetLanguage: string
    code: string
    provider?: any
    url?: string
    headers?: Record<string, string>
  }
  response?: {
    status: number
    statusText?: string
    data: string
    error?: string
    headers?: Record<string, string>
    metadata?: {
      title?: string
      description?: string
      responseTime?: number
      tokenUsage?: any
      model?: string
      provider?: string
    }
    rawResponse?: any // Store complete raw response
    aiProviderDetails?: { // AI provider request/response details
      provider: string
      request: {
        url: string
        method: string
        headers: Record<string, string>
        body: any
        timestamp: string
      }
      response: {
        status: number
        statusText: string
        headers: Record<string, string>
        data: any
        timestamp: string
      }
      responseTime: number
    }
    usage?: {
      inputTokens: number
      outputTokens: number
      totalTokens: number
    }
    agenticDetails?: { // For v3 agentic 2-step workflow
      step1: {
        prompt: string
        response: string
        insightData: any
        usage: any
        time: number
      }
      step2: {
        prompt: string
        response: string
        usage: any
        time: number
      }
    }
  }
  duration?: number
  status: 'pending' | 'success' | 'error'
}

interface ZenSimpleDebugPanelProps {
  isOpen: boolean
  onClose: () => void
  apiCalls: ApiCall[]
  onClear: () => void
}

export const ZenSimpleDebugPanel: React.FC<ZenSimpleDebugPanelProps> = ({
  isOpen,
  onClose,
  apiCalls,
  onClear
}) => {
  if (!isOpen) return null

  const downloadLog = () => {
    const logData = JSON.stringify(apiCalls, null, 2)
    const blob = new Blob([logData], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `zen-simple-debug-${new Date().toISOString()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const formatDuration = (ms?: number) => {
    if (!ms) return '-'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />
      default:
        return <Clock className="w-4 h-4 text-yellow-500 animate-spin" />
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-6xl h-[80vh] mx-4 bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Debug Panel - API Calls</h2>
            <p className="text-sm text-gray-600">{apiCalls.length} call(s) tracked</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadLog}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
              title="Download log"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={onClear}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
              title="Clear all"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
              title="Close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {apiCalls.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">No API calls yet</p>
              <p className="text-sm">Generate some lens insights to see debug info here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {apiCalls.map((call) => (
                <div
                  key={call.id}
                  className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden"
                >
                  {/* Call Header */}
                  <div className="bg-white px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(call.status)}
                        <div>
                          <span className="font-mono text-sm text-gray-900">
                            {call.request.sourceLanguage} → {call.request.targetLanguage}
                          </span>
                          <span className="ml-3 text-sm text-gray-500">
                            {new Date(call.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">
                          Duration: {formatDuration(call.duration)}
                        </span>
                        {call.request.provider && (
                          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                            {call.request.provider.provider || 'default'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Request/Response Details */}
                  <div className="grid md:grid-cols-2 divide-x divide-gray-200">
                    {/* Request */}
                    <div className="p-4">
                      <h4 className="font-semibold text-sm text-gray-700 mb-2">Request</h4>
                      <div className="space-y-2">
                        <div className="text-xs">
                          <span className="font-medium text-gray-600">Type:</span>{' '}
                          <span className="font-mono">{call.request.type}</span>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium text-gray-600">Languages:</span>{' '}
                          <span className="font-mono">
                            {call.request.sourceLanguage} → {call.request.targetLanguage}
                          </span>
                        </div>
                        {call.request.provider?.model && (
                          <div className="text-xs">
                            <span className="font-medium text-gray-600">Model:</span>{' '}
                            <span className="font-mono text-purple-600">
                              {call.request.provider.model}
                            </span>
                          </div>
                        )}
                        <div className="text-xs">
                          <span className="font-medium text-gray-600">Code Length:</span>{' '}
                          <span className="font-mono">{call.request.code.length} chars</span>
                        </div>
                        <details className="text-xs">
                          <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                            Code Preview
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-900 text-gray-100 rounded overflow-x-auto">
                            {call.request.code.substring(0, 500)}
                            {call.request.code.length > 500 && '...'}
                          </pre>
                        </details>
                      </div>
                    </div>

                    {/* Response */}
                    <div className="p-4">
                      <h4 className="font-semibold text-sm text-gray-700 mb-2">Response</h4>
                      {call.response ? (
                        <div className="space-y-2">
                          <div className="text-xs">
                            <span className="font-medium text-gray-600">Status:</span>{' '}
                            <span
                              className={`font-mono ${
                                call.response.status === 200 ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {call.response.status} {call.response.statusText || ''}
                            </span>
                          </div>
                          
                          {/* Response Metadata */}
                          {call.response.metadata && (
                            <div className="text-xs space-y-1">
                              {call.response.metadata.model && (
                                <div>
                                  <span className="font-medium text-gray-600">Model Used:</span>{' '}
                                  <span className="font-mono text-purple-600">{call.response.metadata.model}</span>
                                </div>
                              )}
                              {call.response.metadata.provider && (
                                <div>
                                  <span className="font-medium text-gray-600">Provider:</span>{' '}
                                  <span className="font-mono">{call.response.metadata.provider}</span>
                                </div>
                              )}
                              {call.response.metadata.tokenUsage && (
                                <details className="text-xs">
                                  <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                    Token Usage
                                  </summary>
                                  <pre className="mt-1 p-2 bg-blue-50 text-blue-800 rounded text-xs overflow-x-auto">
                                    {JSON.stringify(call.response.metadata.tokenUsage, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                          )}

                          {/* Agentic 2-Step Details (v3 only) */}
                          {call.response?.agenticDetails && (
                            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                              <h5 className="font-semibold text-sm text-purple-900 mb-2 flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-600 text-white">
                                  Agentic v3
                                </span>
                                2-Step Workflow Details
                              </h5>

                              {/* Step 1: Insight Extraction */}
                              <details className="text-xs mb-2">
                                <summary className="cursor-pointer font-medium text-purple-700 hover:text-purple-900">
                                  Step 1: Insight Extraction ({call.response.agenticDetails.step1.time}ms)
                                </summary>
                                <div className="mt-2 space-y-2 pl-4 border-l-2 border-purple-300">
                                  <div>
                                    <span className="font-medium text-gray-600">Insights Generated:</span>{' '}
                                    <span className="font-mono">{call.response.agenticDetails.step1.insightData?.insights?.length || 'N/A'}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-600">Refined Insights:</span>{' '}
                                    <span className="font-mono">{call.response.agenticDetails.step1.insightData?.refinedInsights?.length || 'N/A'}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-600">Quality Score:</span>{' '}
                                    <span className="font-mono">{call.response.agenticDetails.step1.insightData?.critique?.overallQuality || 'N/A'}</span>
                                  </div>
                                  <details>
                                    <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                      Critique Summary
                                    </summary>
                                    <pre className="mt-1 p-2 bg-white text-purple-800 rounded text-xs overflow-x-auto max-h-60 overflow-y-auto">
                                      {JSON.stringify(call.response.agenticDetails.step1.insightData?.critique, null, 2)}
                                    </pre>
                                  </details>
                                  <details>
                                    <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                      Refined Insights JSON
                                    </summary>
                                    <pre className="mt-1 p-2 bg-white text-purple-800 rounded text-xs overflow-x-auto max-h-60 overflow-y-auto">
                                      {JSON.stringify(call.response.agenticDetails.step1.insightData?.refinedInsights, null, 2)}
                                    </pre>
                                  </details>
                                  <details>
                                    <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                      Step 1 Usage
                                    </summary>
                                    <pre className="mt-1 p-2 bg-white text-purple-800 rounded text-xs overflow-x-auto">
                                      {JSON.stringify(call.response.agenticDetails.step1.usage, null, 2)}
                                    </pre>
                                  </details>
                                </div>
                              </details>

                              {/* Step 2: HTML Composition */}
                              <details className="text-xs">
                                <summary className="cursor-pointer font-medium text-purple-700 hover:text-purple-900">
                                  Step 2: HTML Composition ({call.response.agenticDetails.step2.time}ms)
                                </summary>
                                <div className="mt-2 space-y-2 pl-4 border-l-2 border-purple-300">
                                  <div>
                                    <span className="font-medium text-gray-600">HTML Length:</span>{' '}
                                    <span className="font-mono">{call.response.data?.length || 'N/A'} chars</span>
                                  </div>
                                  <details>
                                    <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                      Step 2 Usage
                                    </summary>
                                    <pre className="mt-1 p-2 bg-white text-purple-800 rounded text-xs overflow-x-auto">
                                      {JSON.stringify(call.response.agenticDetails.step2.usage, null, 2)}
                                    </pre>
                                  </details>
                                </div>
                              </details>
                            </div>
                          )}
                          
                          {/* Response Headers */}
                          {call.response.headers && Object.keys(call.response.headers).length > 0 && (
                            <details className="text-xs">
                              <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                Response Headers
                              </summary>
                              <pre className="mt-1 p-2 bg-gray-100 text-gray-700 rounded text-xs overflow-x-auto">
                                {JSON.stringify(call.response.headers, null, 2)}
                              </pre>
                            </details>
                          )}
                          
                          {call.response.error ? (
                            <div className="text-xs">
                              <span className="font-medium text-red-600">Error:</span>
                              <pre className="mt-1 p-2 bg-red-50 text-red-800 rounded text-xs overflow-x-auto">
                                {call.response.error}
                              </pre>
                            </div>
                          ) : (
                            <>
                              <div className="text-xs">
                                <span className="font-medium text-gray-600">HTML Length:</span>{' '}
                                <span className="font-mono">{call.response.data.length} chars</span>
                              </div>
                              
                              {/* HTML Preview */}
                              <details className="text-xs">
                                <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                  HTML Preview
                                </summary>
                                <pre className="mt-2 p-2 bg-gray-900 text-gray-100 rounded overflow-x-auto text-xs">
                                  {call.response.data.substring(0, 2000)}
                                  {call.response.data.length > 2000 && '...'}
                                </pre>
                              </details>
                              
                              {/* Complete HTML */}
                              <details className="text-xs">
                                <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                  Complete HTML Response
                                </summary>
                                <pre className="mt-2 p-2 bg-gray-900 text-gray-100 rounded overflow-x-auto text-xs max-h-96">
                                  {call.response.data}
                                </pre>
                              </details>
                              
                              {/* Token Usage */}
                              {call.response.usage && (
                                <details className="text-xs">
                                  <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                    Token Usage
                                  </summary>
                                  <div className="mt-2 p-2 bg-blue-50 text-blue-800 rounded text-xs">
                                    <div>Input Tokens: {call.response.usage.inputTokens}</div>
                                    <div>Output Tokens: {call.response.usage.outputTokens}</div>
                                    <div>Total Tokens: {call.response.usage.totalTokens}</div>
                                  </div>
                                </details>
                              )}
                              
                              {/* AI Provider Details */}
                              {call.response.aiProviderDetails && (
                                <details className="text-xs">
                                  <summary className="cursor-pointer font-medium text-orange-600 hover:text-orange-800">
                                    🤖 AI Provider Request/Response (Backend → {call.response.aiProviderDetails.provider})
                                  </summary>
                                  <div className="mt-2 space-y-2">
                                    {/* AI Request */}
                                    <div className="p-2 bg-orange-50 rounded">
                                      <div className="font-medium text-orange-800 mb-1">AI Request:</div>
                                      <div className="text-xs space-y-1">
                                        <div><span className="font-medium">URL:</span> {call.response.aiProviderDetails.request.url}</div>
                                        <div><span className="font-medium">Method:</span> {call.response.aiProviderDetails.request.method}</div>
                                        <div><span className="font-medium">Time:</span> {call.response.aiProviderDetails.request.timestamp}</div>
                                        <details>
                                          <summary className="cursor-pointer font-medium hover:text-orange-900">Headers</summary>
                                          <pre className="mt-1 p-1 bg-white rounded overflow-x-auto">
                                            {JSON.stringify(call.response.aiProviderDetails.request.headers, null, 2)}
                                          </pre>
                                        </details>
                                        <details>
                                          <summary className="cursor-pointer font-medium hover:text-orange-900">Body (Prompt)</summary>
                                          <pre className="mt-1 p-1 bg-white rounded overflow-x-auto max-h-48">
                                            {JSON.stringify(call.response.aiProviderDetails.request.body, null, 2)}
                                          </pre>
                                        </details>
                                      </div>
                                    </div>
                                    
                                    {/* AI Response */}
                                    <div className="p-2 bg-green-50 rounded">
                                      <div className="font-medium text-green-800 mb-1">AI Response:</div>
                                      <div className="text-xs space-y-1">
                                        <div><span className="font-medium">Status:</span> {call.response.aiProviderDetails.response.status} {call.response.aiProviderDetails.response.statusText}</div>
                                        <div><span className="font-medium">Time:</span> {call.response.aiProviderDetails.response.timestamp}</div>
                                        <div><span className="font-medium">Response Time:</span> {call.response.aiProviderDetails.responseTime}ms</div>
                                        <details>
                                          <summary className="cursor-pointer font-medium hover:text-green-900">Response Headers</summary>
                                          <pre className="mt-1 p-1 bg-white rounded overflow-x-auto">
                                            {JSON.stringify(call.response.aiProviderDetails.response.headers, null, 2)}
                                          </pre>
                                        </details>
                                        <details>
                                          <summary className="cursor-pointer font-medium hover:text-green-900">Response Data</summary>
                                          <pre className="mt-1 p-1 bg-white rounded overflow-x-auto max-h-48">
                                            {JSON.stringify(call.response.aiProviderDetails.response.data, null, 2)}
                                          </pre>
                                        </details>
                                      </div>
                                    </div>
                                  </div>
                                </details>
                              )}
                              
                              {/* Raw Response */}
                              {call.response.rawResponse && (
                                <details className="text-xs">
                                  <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                    Raw Response Data
                                  </summary>
                                  <pre className="mt-2 p-2 bg-gray-900 text-gray-100 rounded overflow-x-auto text-xs max-h-96">
                                    {JSON.stringify(call.response.rawResponse, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Waiting for response...</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ZenSimpleDebugPanel